import { toUrlT } from '@execonline-inc/url';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../../Appy';
import { currentUserStore } from '../../../../../../../CurrentUserStore';
import { findLinkT } from '../../../../../../../LinkyLinky';
import { Link } from '../../../../../../../Resource/Types';
import { shareStore } from '../../../Stores/ShareStore';
import { participantsResourceDecoder, userGroupsResourceDecoder } from './Decoders';
import {
  error,
  ParticipantSearchError,
  ParticipantSearchState,
  ParticipantsResource,
  TabType,
  UserGroupsResource,
  waiting,
} from './Types';

export class ParticipantSearchStore {
  @observable state: ParticipantSearchState = waiting();

  constructor() {
    makeObservable(this);
  }

  @computed
  get participantNameSearch(): string {
    return this.state.kind === 'ready' ? this.state.participantNameSearch : '';
  }

  @computed
  get groupNameSearch(): string {
    return this.state.kind === 'ready' ? this.state.groupNameSearch : '';
  }

  @computed
  get activeTab(): TabType {
    return this.state.kind === 'ready' ? this.state.activeTab : 'participants';
  }

  @computed
  get selectedLicenseId(): Maybe<string> {
    return this.state.kind === 'ready' ? this.state.selectedLicenseId : nothing();
  }

  @computed
  get users() {
    if (this.state.kind !== 'ready') return [];

    const { users: searchResults, selectedUsers } = this.state;

    // Get IDs of verified users from text area
    const verifiedUserIds = new Set(shareStore.verifiedUserParticipants.map((u) => u.id));

    // Separate search results into verified (already in text area) and non-verified
    const verifiedUsersInResults: typeof searchResults = [];
    const nonVerifiedUsersInResults: typeof searchResults = [];

    searchResults.forEach((user) => {
      if (verifiedUserIds.has(user.id)) {
        verifiedUsersInResults.push(user);
      } else {
        nonVerifiedUsersInResults.push(user);
      }
    });

    // Get selected users that are NOT in current search results
    const searchResultIds = new Set(searchResults.map((u) => u.id));
    const selectedNotInResults = Array.from(selectedUsers.values()).filter(
      (u) => !searchResultIds.has(u.id),
    );

    // Get verified users from text area that are NOT in search results
    const verifiedUsersNotInResults = shareStore.verifiedUserParticipants
      .filter((u) => !searchResultIds.has(u.id))
      .map((u) => ({
        id: u.id,
        firstName: u.name.split(' ')[0] || '',
        lastName: u.name.split(' ').slice(1).join(' ') || '',
        email: u.email,
        licenseCount: u.licenses.length,
        learningCollectionCount: 0,
        lastLogin: nothing<Date>(),
      }));

    // Combine all pinned users (selected + verified from text area)
    const pinnedUsers = [
      ...selectedNotInResults,
      ...verifiedUsersNotInResults.filter((u) => !selectedUsers.has(u.id)),
      ...verifiedUsersInResults,
    ];

    // Return pinned items first, then non-verified search results
    return [...pinnedUsers, ...nonVerifiedUsersInResults];
  }

  @computed
  get groups() {
    if (this.state.kind !== 'ready') return [];

    const { groups: searchResults, selectedGroups } = this.state;

    // Get IDs of verified groups from text area
    const verifiedGroupIds = new Set(shareStore.verifiedGroupParticipants.map((g) => g.id));

    // Separate search results into verified (already in text area) and non-verified
    const verifiedGroupsInResults: typeof searchResults = [];
    const nonVerifiedGroupsInResults: typeof searchResults = [];

    searchResults.forEach((group) => {
      if (verifiedGroupIds.has(group.id)) {
        verifiedGroupsInResults.push(group);
      } else {
        nonVerifiedGroupsInResults.push(group);
      }
    });

    // Get selected groups that are NOT in current search results
    const searchResultIds = new Set(searchResults.map((g) => g.id));
    const selectedNotInResults = Array.from(selectedGroups.values()).filter(
      (g) => !searchResultIds.has(g.id),
    );

    // Get verified groups from text area that are NOT in search results
    const verifiedGroupsNotInResults = shareStore.verifiedGroupParticipants
      .filter((g) => !searchResultIds.has(g.id))
      .map((g) => ({
        id: g.id,
        name: g.name,
        members: g.members,
      }));

    // Combine all pinned groups (selected + verified from text area)
    const pinnedGroups = [
      ...selectedNotInResults,
      ...verifiedGroupsNotInResults.filter((g) => !selectedGroups.has(g.id)),
      ...verifiedGroupsInResults,
    ];

    // Return pinned items first, then non-verified search results
    return [...pinnedGroups, ...nonVerifiedGroupsInResults];
  }

  @computed
  get selectedUserIds() {
    return this.state.kind === 'ready' ? this.state.selectedUserIds : new Set<number>();
  }

  @computed
  get selectedGroupIds() {
    return this.state.kind === 'ready' ? this.state.selectedGroupIds : new Set<number>();
  }

  @computed
  get searchResultUserIds(): Set<number> {
    if (this.state.kind !== 'ready') return new Set();
    return new Set(this.state.users.map((u) => u.id));
  }

  @computed
  get searchResultGroupIds(): Set<number> {
    if (this.state.kind !== 'ready') return new Set();
    return new Set(this.state.groups.map((g) => g.id));
  }

  @computed
  get pinnedUserIds(): Set<number> {
    if (this.state.kind !== 'ready') return new Set();

    // Combine selected users and verified users from text area
    const selectedIds = this.state.selectedUserIds;
    const verifiedIds = new Set(shareStore.verifiedUserParticipants.map((u) => u.id));

    return new Set([...selectedIds, ...verifiedIds]);
  }

  @computed
  get pinnedGroupIds(): Set<number> {
    if (this.state.kind !== 'ready') return new Set();

    // Combine selected groups and verified groups from text area
    const selectedIds = this.state.selectedGroupIds;
    const verifiedIds = new Set(shareStore.verifiedGroupParticipants.map((g) => g.id));

    return new Set([...selectedIds, ...verifiedIds]);
  }

  @computed
  get selectedUsers() {
    if (this.state.kind !== 'ready') return [];
    return Array.from(this.state.selectedUsers.values());
  }

  @computed
  get selectedGroups() {
    if (this.state.kind !== 'ready') return [];
    return Array.from(this.state.selectedGroups.values());
  }

  @action
  setParticipantNameSearch = (search: string): void => {
    if (this.state.kind === 'ready') {
      this.state.participantNameSearch = search;
      this.fetchParticipants();
    }
  };

  @action
  setGroupNameSearch = (search: string): void => {
    if (this.state.kind === 'ready') {
      this.state.groupNameSearch = search;
      this.fetchGroups();
    }
  };

  @action
  setActiveTab = (tab: TabType): void => {
    if (this.state.kind === 'ready') {
      this.state.activeTab = tab;
    }
  };

  @action
  setSelectedLicenseId = (licenseId: string): void => {
    if (this.state.kind === 'ready') {
      this.state.selectedLicenseId = just(licenseId);
      this.fetchParticipants();
    }
  };

  @action
  clearSelectedLicenseId = (): void => {
    if (this.state.kind === 'ready') {
      this.state.selectedLicenseId = nothing();
      this.fetchParticipants();
    }
  };

  @action
  toggleUserSelection = (userId: number): void => {
    if (this.state.kind === 'ready') {
      const newIdSet = new Set(this.state.selectedUserIds);
      const newUserMap = new Map(this.state.selectedUsers);

      if (newIdSet.has(userId)) {
        newIdSet.delete(userId);
        newUserMap.delete(userId);
      } else {
        // Find the user in current users list (includes both search results and pinned)
        const user = this.users.find((u) => u.id === userId);
        // Only allow selection if user has an active license
        if (user && user.licenseCount > 0) {
          newIdSet.add(userId);
          newUserMap.set(userId, user);
        }
      }

      this.state.selectedUserIds = newIdSet;
      this.state.selectedUsers = newUserMap;
    }
  };

  @action
  toggleGroupSelection = (groupId: number): void => {
    if (this.state.kind === 'ready') {
      const newIdSet = new Set(this.state.selectedGroupIds);
      const newGroupMap = new Map(this.state.selectedGroups);

      if (newIdSet.has(groupId)) {
        newIdSet.delete(groupId);
        newGroupMap.delete(groupId);
      } else {
        // Find the group in current groups list (includes both search results and pinned)
        const group = this.groups.find((g) => g.id === groupId);
        if (group) {
          newIdSet.add(groupId);
          newGroupMap.set(groupId, group);
        }
      }

      this.state.selectedGroupIds = newIdSet;
      this.state.selectedGroups = newGroupMap;
    }
  };

  @action
  toggleAllUsers = (): void => {
    if (this.state.kind === 'ready') {
      const users = this.users;
      const licensedUsers = users.filter((user) => user.licenseCount > 0);
      const { selectedUserIds } = this.state;
      const allSelected = licensedUsers.every((user) => selectedUserIds.has(user.id));

      if (allSelected) {
        this.state.selectedUserIds = new Set();
        this.state.selectedUsers = new Map();
      } else {
        this.state.selectedUserIds = new Set(licensedUsers.map((user) => user.id));
        this.state.selectedUsers = new Map(licensedUsers.map((user) => [user.id, user]));
      }
    }
  };

  @action
  toggleAllGroups = (): void => {
    if (this.state.kind === 'ready') {
      const groups = this.groups;
      const { selectedGroupIds } = this.state;
      const allSelected = groups.every((group) => selectedGroupIds.has(group.id));

      if (allSelected) {
        this.state.selectedGroupIds = new Set();
        this.state.selectedGroups = new Map();
      } else {
        this.state.selectedGroupIds = new Set(groups.map((group) => group.id));
        this.state.selectedGroups = new Map(groups.map((group) => [group.id, group]));
      }
    }
  };

  @action
  clearSelections = (): void => {
    if (this.state.kind === 'ready') {
      this.state.selectedUserIds = new Set();
      this.state.selectedGroupIds = new Set();
      this.state.selectedUsers = new Map();
      this.state.selectedGroups = new Map();
    }
  };

  @action
  reset = (): void => {
    if (this.state.kind === 'ready') {
      this.state.participantNameSearch = '';
      this.state.groupNameSearch = '';
      this.state.selectedLicenseId = nothing();
      this.state.users = [];
      this.state.groups = [];
      this.state.selectedUserIds = new Set();
      this.state.selectedGroupIds = new Set();
      this.state.selectedUsers = new Map();
      this.state.selectedGroups = new Map();
      this.state.activeTab = 'participants';
    }
  };

  private fetchParticipants = (): void => {
    if (this.state.kind !== 'ready') return;

    const { participantNameSearch, selectedLicenseId } = this.state;

    Task.succeed<ParticipantSearchError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('adminstudio-share-learning-collections-search-users'))
      .andThen(this.addParticipantParams(participantNameSearch, selectedLicenseId))
      .andThen(callApi(participantsResourceDecoder, {}))
      .fork(
        action((err: ParticipantSearchError) => {
          console.error('Failed to fetch participants:', err);
          this.state = error('An error occurred, please refresh the page and try again');
        }),
        (resource: ParticipantsResource) => {
          runInAction(() => {
            if (this.state.kind === 'ready') {
              this.state.users = resource.payload.participants;
            }
          });
        },
      );
  };

  private fetchGroups = (): void => {
    if (this.state.kind !== 'ready') return;

    const { groupNameSearch } = this.state;

    Task.succeed<ParticipantSearchError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('adminstudio-share-learning-collections-search-user-groups'))
      .andThen(this.addGroupParams(groupNameSearch))
      .andThen(callApi(userGroupsResourceDecoder, {}))
      .fork(
        action((err: ParticipantSearchError) => {
          console.error('Failed to fetch groups:', err);
          this.state = error('An error occurred, please refresh the page and try again');
        }),
        (resource: UserGroupsResource) => {
          runInAction(() => {
            if (this.state.kind === 'ready') {
              this.state.groups = resource.payload.userGroups;
            }
          });
        },
      );
  };

  private addParticipantParams =
    (nameSearch: string, licenseId: Maybe<string>) =>
    (link: Link): Task<ParticipantSearchError, Link> => {
      return toUrlT(link.href).map((u) => {
        const params = new URLSearchParams();

        if (nameSearch) {
          params.append('name', nameSearch);
        }

        licenseId.do((id) => {
          params.append('license_id', id);
        });

        const queryString = params.toString();
        const separator = u.href.includes('?') ? '&' : '?';
        const newHref = queryString ? `${u.href}${separator}${queryString}` : u.href;

        return { ...link, href: newHref };
      });
    };

  private addGroupParams =
    (nameSearch: string) =>
    (link: Link): Task<ParticipantSearchError, Link> => {
      return toUrlT(link.href).map((u) => {
        const params = new URLSearchParams();

        if (nameSearch) {
          params.append('name', nameSearch);
        }

        const queryString = params.toString();
        const separator = u.href.includes('?') ? '&' : '?';
        const newHref = queryString ? `${u.href}${separator}${queryString}` : u.href;

        return { ...link, href: newHref };
      });
    };
}

const participantSearchStore = new ParticipantSearchStore();

export { participantSearchStore };
